import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQAContext } from "@/contexts/useQAContext";
import { aiService } from "@/lib/ai-service";
import { supabase } from "@/lib/supabase";
import { QAStep } from "../components/QAStep";
import { useQADraftManager } from "../hooks/useQADraftManager";
import { extractQuestionText } from "../utils/qaUtils";

interface ReflectionTheme {
	id: string;
	name: string;
	prompt: string;
}

/**
 * Standalone page for the QA step
 * Supports deep linking and allows users to continue unfinished reflections
 */
export function QAStepPage() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { reflectionId } = useParams();
	const [searchParams] = useSearchParams();

	// QA state from context
	const {
		currentQuestion,
		setCurrentQuestion,
		currentAnswer,
		setCurrentAnswer,
		qaHistory,
		setQaHistory,
		conversationHistory,
		setConversationHistory,
		isLoadingQuestion,
		setIsLoadingQuestion,
		aiError,
		setAiError,
	} = useQAContext();

	// Draft manager for persistence
	const isDraft = !!reflectionId;
	const { saveDraft } = useQADraftManager(reflectionId || null, isDraft);

	// Reflection state (from parent or loaded from DB)
	const reflectionTitleRef = useRef("");
	const reflectionDescriptionRef = useRef("");
	const selectedThemeRef = useRef<ReflectionTheme | null>(null);
	const isTransitioningRef = useRef(false);

	// Load reflection data on mount if continuing a draft
	useEffect(() => {
		if (!user) return;

		const loadReflectionData = async () => {
			if (!reflectionId) {
				// New reflection - try to get data from localStorage or session storage
				try {
					const stored = sessionStorage.getItem("reflectionSessionData");
					if (stored) {
						const data = JSON.parse(stored);
						reflectionTitleRef.current = data.reflectionTitle || "";
						reflectionDescriptionRef.current = data.reflectionDescription || "";
						selectedThemeRef.current = data.selectedTheme || null;
					}
				} catch (error) {
					console.error("Failed to load session data:", error);
				}
				return;
			}

			// Load existing draft from database
			try {
				const { data, error } = await supabase
					.from("reflections")
					.select("title, description, current_state, metadata")
					.eq("id", reflectionId)
					.eq("user_id", user.id)
					.single();

				if (error) throw error;

				if (data) {
					reflectionTitleRef.current = data.title || "";
					reflectionDescriptionRef.current = data.description || "";

					// Parse metadata for theme
					if (data.metadata && typeof data.metadata === "object") {
						selectedThemeRef.current = data.metadata.selectedTheme || null;
					}

					// Restore QA state from current_state if available
					if (data.current_state && typeof data.current_state === "object") {
						const state = data.current_state;
						if (state.qaHistory && Array.isArray(state.qaHistory)) {
							setQaHistory(state.qaHistory);
						}
						if (
							state.conversationHistory &&
							Array.isArray(state.conversationHistory)
						) {
							setConversationHistory(state.conversationHistory);
						}
						if (state.currentQuestion) {
							setCurrentQuestion(state.currentQuestion);
						}
						if (state.currentAnswer) {
							setCurrentAnswer(state.currentAnswer);
						}
					}
				}
			} catch (error) {
				console.error("Failed to load reflection data:", error);
				setAiError("Failed to load reflection. Please try again.");
			}
		};

		loadReflectionData();
	}, [
		reflectionId,
		user,
		setQaHistory,
		setConversationHistory,
		setCurrentQuestion,
		setCurrentAnswer,
		setAiError,
	]);

	// Auto-save QA state periodically
	useEffect(() => {
		const interval = setInterval(() => {
			if (isDraft && qaHistory.length > 0) {
				saveDraft({
					qaHistory,
					conversationHistory,
					currentQuestion,
					currentAnswer,
				});
			}
		}, 30000); // Auto-save every 30 seconds

		return () => clearInterval(interval);
	}, [
		isDraft,
		qaHistory,
		conversationHistory,
		currentQuestion,
		currentAnswer,
		saveDraft,
	]);

	const handleSubmitAnswer = async () => {
		if (!currentAnswer.trim()) return;

		setIsLoadingQuestion(true);
		setAiError(null);

		try {
			// Add current Q&A to history
			const newQA = { question: currentQuestion, answer: currentAnswer };
			const updatedHistory = [...qaHistory, newQA];
			setQaHistory(updatedHistory);

			// Update conversation history
			const updatedConversation = [
				...conversationHistory,
				{ role: "user" as const, content: currentAnswer },
			];
			setConversationHistory(updatedConversation);

			const questionCount = updatedHistory.length;

			// Check if we've reached max questions (6)
			if (questionCount >= 6) {
				isTransitioningRef.current = true;

				try {
					// Generate summary
					const summaryResponse =
						await aiService.generateReflectionSummary(updatedHistory);

					let generatedTitle = "";
					let generatedDescription = "";

					if (
						summaryResponse &&
						typeof summaryResponse === "object" &&
						(summaryResponse as any).title &&
						(summaryResponse as any).description
					) {
						generatedTitle = (summaryResponse as any).title;
						generatedDescription = (summaryResponse as any).description;
					} else if (typeof summaryResponse === "string") {
						generatedTitle = reflectionTitleRef.current || "My Reflection";
						generatedDescription = summaryResponse;
					} else if (summaryResponse && typeof summaryResponse === "object") {
						const anyResp: any = summaryResponse as any;
						const fallbackTitle =
							(reflectionTitleRef.current &&
								reflectionTitleRef.current.trim()) ||
							"My Reflection";
						generatedTitle =
							anyResp.title || anyResp.name || anyResp.heading || fallbackTitle;
						generatedDescription =
							anyResp.description ||
							anyResp.summary ||
							anyResp.content ||
							anyResp.text ||
							anyResp.response ||
							"";
					}

					if (generatedTitle) reflectionTitleRef.current = generatedTitle;
					if (generatedDescription)
						reflectionDescriptionRef.current = generatedDescription;

					// Save reflection state and transition
					sessionStorage.setItem(
						"reflectionSessionData",
						JSON.stringify({
							reflectionTitle: reflectionTitleRef.current,
							reflectionDescription: reflectionDescriptionRef.current,
							selectedTheme: selectedThemeRef.current,
							qaHistory: updatedHistory,
							conversationHistory: updatedConversation,
						}),
					);

					setIsLoadingQuestion(false);

					// Navigate to next step (description)
					if (reflectionId) {
						navigate(`/reflection/${reflectionId}/description`);
					} else {
						navigate("/reflection/new/description");
					}
				} catch (error) {
					console.error("Error generating summary:", error);
					const errorMessage =
						error instanceof Error ? error.message : "Unknown error";
					setAiError(`Unable to generate reflection summary: ${errorMessage}`);
					setIsLoadingQuestion(false);
					isTransitioningRef.current = false;
				}

				return;
			}

			// Get next question
			setCurrentQuestion("");
			setCurrentAnswer("");

			const isAISuggested = searchParams.get("suggested") === "true";
			let themePromptToSend = selectedThemeRef.current?.prompt;

			if (isAISuggested) {
				const title = decodeURIComponent(searchParams.get("title") || "");
				const description = decodeURIComponent(
					searchParams.get("description") || "",
				);
				const reasoning = decodeURIComponent(
					searchParams.get("reasoning") || "",
				);

				themePromptToSend = `FOCUSED REFLECTION TOPIC: ${title}

DESCRIPTION: ${description}

AI ANALYSIS REASONING: ${reasoning}

This reflection topic was specifically identified from the user's daily check-in patterns. Focus on helping them explore their personal experience with this topic in depth.`;
			}

			const response = await aiService.askReflectionQuestion(
				updatedHistory,
				themePromptToSend,
			);

			if (response.question) {
				setCurrentQuestion(response.question);
				setConversationHistory([
					...updatedConversation,
					{ role: "assistant" as const, content: response.question },
				]);
			} else {
				throw new Error("AI service did not return a question");
			}
		} catch (error) {
			console.error("Error submitting answer:", error);
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			setAiError(
				`AI conversation service error: ${errorMessage}. Please contact support.`,
			);
		} finally {
			setIsLoadingQuestion(false);
			isTransitioningRef.current = false;
		}
	};

	if (!user) {
		return (
			<div className="container mx-auto px-4 py-8 text-center">
				<p className="text-gray-600">Please log in to continue.</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8">
			<QAStep
				currentQuestion={currentQuestion}
				currentAnswer={currentAnswer}
				qaHistory={qaHistory}
				isLoadingQuestion={isLoadingQuestion}
				aiError={aiError}
				currentQuestionNumber={qaHistory.length + 1}
				onAnswerChange={setCurrentAnswer}
				onSubmitAnswer={handleSubmitAnswer}
				subtitle="Carefully consider and answer the questions about your reflection"
			/>
		</div>
	);
}
